import { Loader, Menu } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
    IconArrowDown,
    IconArrowUp,
    IconDots,
    IconPencil,
    IconPlus,
    IconTrash,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FirewallRule, RulePosition } from '../../../../../@types/firewall-rule';
import { Button, ConfirmActionModal } from '../../../../../components/Atoms';
import DialogBox from '../../../../../components/Atoms/DialogBox/DialogBox';
import { useDeleteSecurityGroupRuleMutate } from '../../../../../hooks/CloudComputeHooks/useDeleteSecurityGroupRuleMutate';
import { useSecurityGroupRulesQuery } from '../hooks/useSecurityGroupRulesQuery';
import SecurityGroupRuleModal from './SecurityGroupRuleModal';

interface SecurityGroupRulesCardProps {
    groupId: string;
}

const GRID = 'grid grid-cols-[120px_90px_70px_110px_150px_150px_1fr_40px] gap-3 items-center';
// Floor for the scroll container: fixed tracks (730) + gaps (7x12) + px-4 (32) + a 160px floor
// for the description track. Keep in sync with GRID.
const GRID_MIN_W = 'min-w-[1010px]';

function DirectionPill({ direction }: { direction: 'in' | 'out' }) {
    const { t } = useTranslation('cloudComputeInstance');
    if (direction === 'in') {
        return (
            <span
                className="inline-flex w-fit items-center gap-1 px-2 py-1 rounded-full text-xs"
                style={{ backgroundColor: '#3b82f626', color: 'var(--color-status-info)' }}
            >
                <IconArrowDown size={12} />
                {t('firewall.direction.inbound')}
            </span>
        );
    }
    return (
        <span
            className="inline-flex w-fit items-center gap-1 px-2 py-1 rounded-full text-xs"
            style={{ backgroundColor: '#a78bfa26', color: 'var(--color-status-violet)' }}
        >
            <IconArrowUp size={12} />
            {t('firewall.direction.outbound')}
        </span>
    );
}

export default function SecurityGroupRulesCard({ groupId }: SecurityGroupRulesCardProps) {
    const { data, isPending } = useSecurityGroupRulesQuery(groupId);
    const deleteMutation = useDeleteSecurityGroupRuleMutate();
    const { t } = useTranslation('cloudComputeSecurityGroup');
    const { t: tInstance } = useTranslation('cloudComputeInstance');

    const [ruleModalOpened, { open: openRuleModal, close: closeRuleModal }] = useDisclosure();
    const [editingRule, setEditingRule] = useState<(FirewallRule & RulePosition) | null>(null);

    const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure();
    const [deletingPosition, setDeletingPosition] = useState<number | null>(null);

    const rows = data?.rows ?? [];

    const openCreate = () => {
        setEditingRule(null);
        openRuleModal();
    };

    const openEdit = (rule: FirewallRule & RulePosition) => {
        setEditingRule(rule);
        openRuleModal();
    };

    const askDelete = (position: number) => {
        setDeletingPosition(position);
        openDelete();
    };

    const confirmDelete = () => {
        if (deletingPosition == null) return;
        deleteMutation.mutate(
            { groupId, position: deletingPosition },
            {
                onSuccess: () => {
                    closeDelete();
                    setDeletingPosition(null);
                },
            }
        );
    };

    const addButton = (
        <Button size="sm" variant="primary" leftIcon={<IconPlus size={16} />} onClick={openCreate}>
            {t('actions.addRule')}
        </Button>
    );

    return (
        <DialogBox
            label={t('rulesCard.title')}
            description={t('rulesCard.rulesCount', { count: rows.length })}
            rightDescription={addButton}
            classNames=""
        >
            <div
                className="w-full overflow-x-auto"
                tabIndex={0}
                role="region"
                aria-label={t('rulesCard.title')}
            >
                <div className={GRID_MIN_W}>
                    <div
                        className={`${GRID} w-full px-4 py-3 text-xs text-fg/68 uppercase tracking-wider text-nowrap border-b-[0.5px] border-line`}
                    >
                        <span>{t('rule.direction')}</span>
                        <span>{t('rule.protocol')}</span>
                        <span>{t('rule.port')}</span>
                        <span>{t('rule.interface')}</span>
                        <span>{t('rule.source')}</span>
                        <span>{t('rule.destination')}</span>
                        <span>{t('rule.description')}</span>
                        <span />
                    </div>

                    {rows.map((r) => {
                        return (
                            <div
                                key={r.position}
                                className={`${GRID} w-full px-4 py-3 border-t-[0.5px] border-line`}
                            >
                                <DirectionPill direction={r.direction} />
                                <span className="text-sm text-fg uppercase">
                                    {r.protocol ? r.protocol : t('rulesCard.allProtocol')}
                                </span>
                                <span className="text-sm text-fg">
                                    {r.destination_port || r.source_port || t('rulesCard.allPort')}
                                </span>
                                <span className="text-sm text-fg">
                                    {tInstance(
                                        r.iface === 'net1'
                                            ? 'firewall.interfaceOption.private'
                                            : 'firewall.interfaceOption.public'
                                    )}
                                </span>
                                <span className="text-sm text-fg font-mono truncate">
                                    {r.source || '0.0.0.0/0'}
                                </span>
                                <span className="text-sm text-fg font-mono truncate">
                                    {r.destination || '0.0.0.0/0'}
                                </span>
                                <span className="text-sm text-fg/80 truncate">
                                    {r.description || '—'}
                                </span>
                                <Menu position="bottom-end" withinPortal>
                                    <Menu.Target>
                                        <button
                                            type="button"
                                            aria-label={t('actions.rowActions')}
                                            className="w-8 h-8 inline-flex items-center justify-center rounded-md text-fg/68 hover:bg-surface-3 hover:text-fg transition-colors"
                                        >
                                            <IconDots size={16} />
                                        </button>
                                    </Menu.Target>
                                    <Menu.Dropdown>
                                        <Menu.Item
                                            leftSection={<IconPencil size={14} />}
                                            onClick={() => openEdit(r)}
                                        >
                                            {t('actions.editRule')}
                                        </Menu.Item>
                                        <Menu.Item
                                            color="var(--color-status-danger)"
                                            leftSection={<IconTrash size={14} />}
                                            onClick={() => askDelete(r.position)}
                                        >
                                            {t('actions.deleteRule')}
                                        </Menu.Item>
                                    </Menu.Dropdown>
                                </Menu>
                            </div>
                        );
                    })}
                </div>
            </div>

            {isPending && !data ? (
                <div className="w-full p-8 flex justify-center">
                    <Loader size={20} color="var(--fg)" />
                </div>
            ) : rows.length === 0 ? (
                <div className="w-full p-8 text-sm text-fg/68 text-center">
                    {t('rulesCard.emptyPrefix')}{' '}
                    <b className="text-fg">{t('rulesCard.emptyAction')}</b>{' '}
                    {t('rulesCard.emptySuffix')}
                </div>
            ) : null}

            <SecurityGroupRuleModal
                opened={ruleModalOpened}
                onClose={() => {
                    closeRuleModal();
                    setEditingRule(null);
                }}
                groupId={groupId}
                rule={editingRule ?? undefined}
            />
            <ConfirmActionModal
                opened={deleteOpened}
                onClose={closeDelete}
                title={t('deleteRuleModal.title')}
                message={t('deleteRuleModal.message')}
                confirmLabel={t('deleteRuleModal.confirmLabel')}
                confirmVariant="danger"
                isLoading={deleteMutation.isPending}
                onConfirm={confirmDelete}
            />
        </DialogBox>
    );
}
